function markVideoReady(video) {
    const panel = video.closest('video-panel');
    if (panel) panel.dataset.videoReady = 'true';
}

function waitForVideoReady(video) {
    if (video.readyState >= 2) {
        markVideoReady(video);
        return Promise.resolve(true);
    }

    return new Promise((resolve) => {
        const cleanup = () => {
            video.removeEventListener('loadeddata', handleReady);
            video.removeEventListener('canplay', handleReady);
            video.removeEventListener('error', handleError);
        };
        const handleReady = () => {
            cleanup();
            markVideoReady(video);
            resolve(true);
        };
        const handleError = () => {
            cleanup();
            resolve(false);
        };

        video.addEventListener('loadeddata', handleReady, { once: true });
        video.addEventListener('canplay', handleReady, { once: true });
        video.addEventListener('error', handleError, { once: true });
    });
}

function loadVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return Promise.resolve(false);
    if (video.dataset.loaded === 'true') return waitForVideoReady(video);

    const sources = video.querySelectorAll('source[data-src]');
    let hasSource = false;

    sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        hasSource = true;
    });

    if (video.dataset.src) {
        video.src = video.dataset.src;
        video.removeAttribute('data-src');
        hasSource = true;
    }

    if (!hasSource) return waitForVideoReady(video);

    video.dataset.loaded = 'true';
    video.addEventListener('loadeddata', () => markVideoReady(video), { once: true });
    video.addEventListener('playing', () => markVideoReady(video), { once: true });
    video.load();

    if (video.hasAttribute('autoplay')) {
        video.play().catch(() => {});
    }

    return waitForVideoReady(video);
}

function initLazyVideos(root = document) {
    const videos = root.querySelectorAll('video[data-src], video source[data-src]');
    if (!videos.length) return;

    const videoElements = Array.from(videos, (element) => element.closest('video') || element);
    const uniqueVideos = [...new Set(videoElements)];

    if (!('IntersectionObserver' in window)) {
        uniqueVideos.forEach(loadVideo);
        return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;

            const video = entry.target;
            loadVideo(video);
            obs.unobserve(video);
        });
    }, { rootMargin: '200px 0px', threshold: 0.01 });

    uniqueVideos.forEach(video => {
        if (video.readyState >= 2) {
            markVideoReady(video);
            return;
        }

        observer.observe(video);
    });
}

function reloadVideo(video) {
    video.load();
    if (video.hasAttribute('autoplay')) {
        video.play().catch(() => {});
    }
}

// Videos that reloadResponsiveVideos skipped because they weren't displayed. Each one reloads,
// with the source for the size at that moment, as soon as it's on screen again.
const reloadWhenShown = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            obs.unobserve(entry.target);
            reloadVideo(entry.target);
        }
    }, { rootMargin: '200px 0px' })
    : null;

function reloadResponsiveVideos() {
    const videos = document.querySelectorAll('video[data-responsive-video][data-loaded="true"]');

    for (const video of videos) {
        // This size change replaces any reload still waiting from an earlier one.
        reloadWhenShown?.unobserve(video);

        // A video that isn't displayed at the new size (e.g. the homepage slideshow on phones
        // while the mobile hero experiment is on) mustn't fetch its source for that size. It's
        // paused and reloads once it's shown, which also covers a video hidden for some other
        // reason (an inactive slide, in browsers that report those as not displayed) coming
        // back without another size change.
        if (reloadWhenShown && video.getClientRects().length === 0) {
            video.pause();
            reloadWhenShown.observe(video);
            continue;
        }

        reloadVideo(video);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLazyVideos, { once: true });
} else {
    initLazyVideos();
}

const responsiveVideoQuery = window.matchMedia?.('(max-width: 749px)');
if (responsiveVideoQuery?.addEventListener) {
    responsiveVideoQuery.addEventListener('change', reloadResponsiveVideos);
} else if (responsiveVideoQuery?.addListener) {
    responsiveVideoQuery.addListener(reloadResponsiveVideos);
}

window.HiNoteLazyVideo = {
    init: initLazyVideos,
    load: loadVideo,
};

document.addEventListener('shopify:section:load', (event) => {
    initLazyVideos(event.target);
});
